import { cnpj, cpf } from 'cpf-cnpj-validator';
import { CHECKOUT_FIELD_LIMITS } from '../Constants/checkoutFieldLimits';
import { PaymentMethod } from '../Types';
import { BoletoPayload } from '../Services/BoletoService';
import { getMaxParcelas } from './cardBrand';
import { getPrimeiroVencimento } from './boletoConfig';

export interface BoletoFormState {
  pagadorCpfCnpj: string;
  pagadorNome: string;
  emailContato: string;
  holderPhone: string;
  installments: number;
  holderCep: string;
  holderAddress: string;
  holderNumber: string;
  holderComplement: string;
  holderNeighborhood: string;
  holderState: string;
  holderCity: string;
}

function maxLengthError(value: string, max: number, label: string): string | null {
  if (value.length > max) return `${label} deve ter no máximo ${max} caracteres.`;
  return null;
}

export function validateBoletoCheckoutForm(
  form: BoletoFormState,
  method: PaymentMethod
): Record<string, string> {
  const newErrors: Record<string, string> = {};
  const maxParcelas = getMaxParcelas(method);

  const docDigits = form.pagadorCpfCnpj.replace(/\D/g, '');
  if (!docDigits) {
    newErrors.pagadorCpfCnpj = 'CPF/CNPJ é obrigatório.';
  } else if (!cpf.isValid(docDigits) && !cnpj.isValid(docDigits)) {
    newErrors.pagadorCpfCnpj = 'CPF ou CNPJ inválido.';
  }

  if (!form.pagadorNome.trim()) {
    newErrors.pagadorNome = 'Nome completo é obrigatório.';
  } else if (/\d/.test(form.pagadorNome)) {
    newErrors.pagadorNome = 'O nome não pode conter números.';
  } else if (form.pagadorNome.trim().split(/\s+/).length < 2) {
    newErrors.pagadorNome = 'Informe o nome completo.';
  } else {
    const err = maxLengthError(form.pagadorNome, CHECKOUT_FIELD_LIMITS.pagadorNome, 'Nome completo');
    if (err) newErrors.pagadorNome = err;
  }

  if (!form.emailContato.trim()) {
    newErrors.emailContato = 'E-mail é obrigatório.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.emailContato)) {
    newErrors.emailContato = 'E-mail inválido.';
  } else {
    const err = maxLengthError(form.emailContato, CHECKOUT_FIELD_LIMITS.emailContato, 'E-mail');
    if (err) newErrors.emailContato = err;
  }

  const phoneDigits = form.holderPhone.replace(/\D/g, '');
  if (!phoneDigits) {
    newErrors.holderPhone = 'Celular é obrigatório.';
  } else if (phoneDigits.length < 10) {
    newErrors.holderPhone = 'Celular inválido.';
  }

  if (!form.installments || form.installments < 1 || form.installments > maxParcelas) {
    newErrors.installments = `Selecione de 1 a ${maxParcelas} parcelas.`;
  }

  const primeiroVencimento = getPrimeiroVencimento(method);
  if (!primeiroVencimento) {
    newErrors.boletoConfig = 'Primeiro vencimento não configurado no link de pagamento.';
  }

  const cepDigits = form.holderCep.replace(/\D/g, '');
  if (!cepDigits) {
    newErrors.holderCep = 'CEP é obrigatório.';
  } else if (cepDigits.length !== 8) {
    newErrors.holderCep = 'CEP inválido.';
  }

  if (!form.holderAddress.trim()) {
    newErrors.holderAddress = 'Endereço é obrigatório.';
  } else {
    const err = maxLengthError(form.holderAddress, CHECKOUT_FIELD_LIMITS.holderAddress, 'Endereço');
    if (err) newErrors.holderAddress = err;
  }

  if (!form.holderNumber.trim()) {
    newErrors.holderNumber = 'Número é obrigatório.';
  } else {
    const err = maxLengthError(form.holderNumber, CHECKOUT_FIELD_LIMITS.holderNumber, 'Número');
    if (err) newErrors.holderNumber = err;
  }

  if (form.holderComplement.trim()) {
    const err = maxLengthError(
      form.holderComplement,
      CHECKOUT_FIELD_LIMITS.holderComplement,
      'Complemento'
    );
    if (err) newErrors.holderComplement = err;
  }

  if (!form.holderNeighborhood.trim()) {
    newErrors.holderNeighborhood = 'Bairro é obrigatório.';
  } else {
    const err = maxLengthError(
      form.holderNeighborhood,
      CHECKOUT_FIELD_LIMITS.holderNeighborhood,
      'Bairro'
    );
    if (err) newErrors.holderNeighborhood = err;
  }

  if (!form.holderState.trim()) {
    newErrors.holderState = 'Estado é obrigatório.';
  }

  if (!form.holderCity.trim()) {
    newErrors.holderCity = 'Município é obrigatório.';
  } else {
    const err = maxLengthError(form.holderCity, CHECKOUT_FIELD_LIMITS.holderCity, 'Município');
    if (err) newErrors.holderCity = err;
  }

  return newErrors;
}

export function buildBoletoPayload(
  finalValue: number,
  method: PaymentMethod,
  form: BoletoFormState
): BoletoPayload {
  const primeiroVencimento = getPrimeiroVencimento(method);
  if (!primeiroVencimento) {
    throw new Error('Primeiro vencimento não configurado no link de pagamento.');
  }

  const pagador: BoletoPayload['pagador'] = {
    'cpf-cnpj': form.pagadorCpfCnpj.replace(/\D/g, ''),
    nome: form.pagadorNome.trim(),
    telefone: form.holderPhone.replace(/\D/g, ''),
    email: form.emailContato.trim(),
    cep: form.holderCep.replace(/\D/g, ''),
    endereco: form.holderAddress.trim(),
    numero: form.holderNumber.trim(),
    bairro: form.holderNeighborhood.trim(),
    estado: form.holderState.trim().toUpperCase(),
    cidade: form.holderCity.trim(),
  };

  const complemento = form.holderComplement.trim();
  if (complemento) {
    pagador.complemento = complemento;
  }

  return {
    metodo: 'boleto',
    valor: finalValue,
    parcelas: form.installments,
    'primeiro-vencimento': primeiroVencimento,
    pagador,
  };
}
